/**
 * RadarChartViz Component
 *
 * Spider/radar chart visualization comparing individual performance
 * against team average across evaluation pillars.
 * Uses Recharts for rendering.
 */
import React from 'react'
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts'
import { cn } from '@/lib/utils'
import type { RadarChartPoint, EvaluationPillar } from '@/types/radar'

interface RadarChartVizProps {
  data: RadarChartPoint[]
  individualAverage: number
  teamAverage: number
  className?: string
}

export function RadarChartViz({
  data,
  individualAverage,
  teamAverage,
  className,
}: RadarChartVizProps) {
  // Transform data for Recharts
  const chartData = data.map((point) => ({
    pillar: point.label,
    individual: point.individual,
    team: point.team,
    fullMark: 5,
  }))

  const delta = individualAverage - teamAverage
  const deltaColor = delta >= 0 ? 'text-green-500' : 'text-red-500'

  return (
    <div className={cn('space-y-4', className)} data-testid="radar-chart">
      {/* Chart */}
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
            <PolarGrid stroke="#374151" />
            <PolarAngleAxis
              dataKey="pillar"
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 5]}
              tick={{ fill: '#6B7280', fontSize: 10 }}
              tickCount={6}
            />
            <Radar
              name="Individual"
              dataKey="individual"
              stroke="#00D9FF"
              fill="#00D9FF"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Radar
              name="Equipe"
              dataKey="team"
              stroke="#6B7280"
              fill="#6B7280"
              fillOpacity={0.1}
              strokeWidth={2}
              strokeDasharray="5 5"
            />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              iconType="circle"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
              }}
              labelStyle={{ color: '#FFFFFF' }}
              itemStyle={{ color: '#9CA3AF' }}
              formatter={(value: number) => value.toFixed(2)}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend explanation */}
      <div className="flex items-center justify-center gap-8 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-brand-primary" />
          <span className="text-white">Individual</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-brand-gray border-2 border-dashed border-brand-gray" />
          <span className="text-brand-gray">Equipe</span>
        </div>
      </div>

      {/* Averages summary */}
      <div className="flex items-center justify-around pt-4 border-t border-brand-gray/20">
        <div className="text-center">
          <p className="text-sm text-brand-gray">Média Individual</p>
          <p className="text-2xl font-bold text-brand-primary">{individualAverage.toFixed(2)}</p>
        </div>
        <div className="text-center">
          <p className="text-sm text-brand-gray">Média Equipe</p>
          <p className="text-2xl font-bold text-brand-gray">{teamAverage.toFixed(2)}</p>
        </div>
        <div className="text-center">
          <p className="text-sm text-brand-gray">Delta</p>
          <p className={cn('text-2xl font-bold', deltaColor)}>
            {delta >= 0 ? '+' : ''}{delta.toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Compact version for smaller spaces
 */
export function RadarChartCompact({
  data,
  className,
}: {
  data: RadarChartPoint[]
  className?: string
}) {
  const chartData = data.map((point) => ({
    pillar: point.label.substring(0, 3).toUpperCase(),
    individual: point.individual,
    team: point.team,
    fullMark: 5,
  }))

  return (
    <div className={cn('h-[200px]', className)}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={chartData}>
          <PolarGrid stroke="#374151" />
          <PolarAngleAxis dataKey="pillar" tick={{ fill: '#6B7280', fontSize: 10 }} />
          <Radar
            dataKey="individual"
            stroke="#00D9FF"
            fill="#00D9FF"
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Radar
            dataKey="team"
            stroke="#6B7280"
            fill="transparent"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  )
}
